import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SearchOverlay from './SearchOverlay';
import './Menu.css';

const SEARCH_ICON = 'https://cdn-icons-png.flaticon.com/512/639/639426.png';
const CLOSE_ICON = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="%230b2f3c" opacity="0.8"/></svg>');

function Menu() {
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [currentTime] = useState(new Date());
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pillRect, setPillRect] = useState(null);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 200);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setPillRect(null);
  }, []);

  const handleIconClick = useCallback(() => {
    if (searchQuery.trim()) {
      setSearchQuery('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else if (searchOpen) {
      closeSearch();
    } else {
      openSearch();
    }
  }, [searchQuery, searchOpen, closeSearch, openSearch]);

  const handleSearchSelect = useCallback((systemId, meshKey) => {
    navigate(`/${systemId}`, { state: { focusMesh: meshKey } });
  }, [navigate]);

  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSearch(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, closeSearch]);

  useEffect(() => {
    if (!searchOpen) {
      setPillRect(null);
      return;
    }
    const timer = setTimeout(() => {
      if (searchWrapperRef.current) {
        const r = searchWrapperRef.current.getBoundingClientRect();
        setPillRect({ left: r.left, bottom: r.bottom, width: r.width });
      }
    }, 420);
    return () => clearTimeout(timer);
  }, [searchOpen]);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      if (!searchQuery.trim()) closeSearch();
    }, 180);
  }, [searchQuery, closeSearch]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
  }, []);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (e.key === 'Enter') {
      if (searchQuery.trim()) {
        searchInputRef.current?.blur();
      }
    }
  }, [closeSearch, searchQuery]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCardClick = (path) => {
    navigate(path);
  };

  const rawName = user?.firstName || user?.name || user?.email?.split('@')[0] || 'Guest';
  const firstName = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  const getTimeInfo = () => {
    const hour = currentTime.getHours();
    let greeting = 'Welcome';
    if (hour >= 5 && hour < 12) greeting = 'Good morning';
    else if (hour >= 12 && hour < 17) greeting = 'Good afternoon';
    else if (hour >= 17 && hour < 21) greeting = 'Good evening';
    return greeting;
  };

  const greeting = getTimeInfo();

  const pillInner = (
    <>
      <input
        ref={searchInputRef}
        type="text"
        className="search-input"
        placeholder="Search anatomy models..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleInputKeyDown}
      />
      <button
        type="button"
        className="search-icon-btn"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleIconClick}
        title={searchQuery.trim() ? 'Clear' : searchOpen ? 'Close' : 'Search'}
        aria-label={searchQuery.trim() ? 'Clear search' : searchOpen ? 'Close search' : 'Search body parts'}
      >
        {searchQuery.trim() ? (
          <img src={CLOSE_ICON} alt="" className="search-icon-img" />
        ) : (
          <img src={SEARCH_ICON} alt="" className="search-icon-img" />
        )}
      </button>
    </>
  );

  return (
    <div className="menu" ref={menuRef}>
      <div className="animated-gradient-bg" />

      <div className="menu-waves">
        <div className="menu-wave menu-wave-top"></div>
        <div className="menu-wave menu-wave-middle"></div>
        <div className="menu-wave menu-wave-bottom"></div>
      </div>

          <button
            className="menu-logout-icon-btn"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
          >
            <img src="https://cdn-icons-png.flaticon.com/512/9070/9070510.png" alt="Logout" />
          </button>

          <div
            className={`search-pill${searchOpen ? ' is-open' : ''}`}
            ref={searchWrapperRef}
          >
            {pillInner}
          </div>

      <div className="menu-content">

          <motion.img
            src="/DarkLogo.png"
            alt="METATIP emblem"
            className="organs-logo"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="hero-greeting"
          >
            <h2 className="greeting-pre">{greeting},</h2>
            <h1 className="greeting-main-title">
              {firstName}<span className="shimmer-dot">.</span>
            </h1>
            <p className="greeting-sub">
              {fullName
                ? `Welcome, ${fullName}. Your 3D & AR anatomy workspace is ready.`
                : 'Your 3D & AR anatomy workspace is ready.'}
            </p>
          </motion.div>

          <div className="section-title slide-up delay-3">Computer-Generated Models</div>
          <div className="section-sub slide-up delay-3">High-fidelity anatomical structures created digitally</div>
          <div className="grid grid-systems">
            <div className="card slide-up delay-4" onClick={() => handleCardClick('/skeleton')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/7437/7437376.png" alt="Skeleton" />
              </div>
              <div className="card-title">Skeleton</div>
              <div className="card-sub">Complete Virtual Bone Structure</div>
            </div>

            <div className="card slide-up delay-5" onClick={() => handleCardClick('/brain')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/6969/6969710.png" alt="Brain" />
              </div>
              <div className="card-title">Brain</div>
              <div className="card-sub">Explore the Neural Network</div>
            </div>

            <div className="card slide-up delay-6" onClick={() => handleCardClick('/muscles')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/7671/7671649.png" alt="Muscles" />
              </div>
              <div className="card-title">Muscles</div>
              <div className="card-sub">Muscular System Anatomy</div>
            </div>

            <div className="card slide-up delay-7" onClick={() => handleCardClick('/cardiovascularsystem')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/14323/14323111.png" alt="Cardiovascular System" />
              </div>
              <div className="card-title">Cardiovascular System</div>
              <div className="card-sub">Cardiovascular Anatomy</div>
            </div>

            <div className="card slide-up delay-8" onClick={() => handleCardClick('/nervoussystem')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/11603/11603901.png" alt="Nervous System" />
              </div>
              <div className="card-title">Nervous System</div>
              <div className="card-sub">Neural Pathways &amp; Nerves</div>
            </div>

            <div className="card slide-up delay-9" onClick={() => handleCardClick('/lymphaticsystem')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/3323/3323127.png" alt="Lymphatic System" />
              </div>
              <div className="card-title">Lymphatic System</div>
              <div className="card-sub">Immune Network Anatomy</div>
            </div>

            <div className="card slide-up delay-11" onClick={() => handleCardClick('/digestivesystem')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/10172/10172608.png" alt="Digestive System" />
              </div>
              <div className="card-title">Digestive System</div>
              <div className="card-sub">Digestive Tract Anatomy</div>
            </div>

            <div className="card slide-up delay-12" onClick={() => handleCardClick('/respiratorysystem')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/9774/9774039.png" alt="Respiratory System" />
              </div>
              <div className="card-title">Respiratory System</div>
              <div className="card-sub">Lungs &amp; Respiratory Tract</div>
            </div>

            <div className="card slide-up delay-13" onClick={() => handleCardClick('/urinarysystem')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/17306/17306383.png" alt="Urinary System" />
              </div>
              <div className="card-title">Urinary System</div>
              <div className="card-sub">Renal &amp; Urinary Anatomy</div>
            </div>
          </div>

          <div className="section-title slide-up delay-5">Real Anatomy Models</div>
          <div className="section-sub slide-up delay-5">MRI &amp; CT scan data transformed into true-to-life 3D</div>
          <div className="grid">
            <div className="card slide-up delay-6" onClick={() => handleCardClick('/neck')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/4533/4533534.png" alt="Neck" />
              </div>
              <div className="card-title">Neck</div>
              <div className="card-sub">3D Cervical Spine from MRI Scan</div>
            </div>

            <div className="card slide-up delay-7" onClick={() => handleCardClick('/skullchest')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/10299/10299270.png" alt="Skull & Chest" />
              </div>
              <div className="card-title">Skull &amp; Chest</div>
              <div className="card-sub">3D Upper Body from MRI Scan</div>
            </div>

            <div className="card slide-up delay-8" onClick={() => handleCardClick('/chest')}>
              <div className="icon">
                <img src="https://cdn-icons-png.flaticon.com/512/7220/7220470.png" alt="Chest" />
              </div>
              <div className="card-title">Chest</div>
              <div className="card-sub">3D Thoracic Cavity from MRI Scan</div>
            </div>
           </div>
       </div>

      <AnimatePresence>
        {searchOpen && (
          <SearchOverlay
            query={searchQuery}
            onSelect={handleSearchSelect}
            onClose={closeSearch}
            anchorRect={pillRect}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Menu;
